import React, { useState, useEffect, Suspense, lazy } from 'react';
import {
  Spinner,
  ColorModeProvider,
  Tooltip,
  Inline,
  Divider,
  IconButton,
  Button,
  DialogActions,
  Dialog,
  DialogHeader,
} from '@airbus/components-react';
import { Add, ContentCopy, Delete, Search } from '@mui/icons-material';
import SettingsIcon from '@mui/icons-material/Settings';
import { customValidator, formatDate, postRequest } from '../../utils';
import { useColumnContextAPI } from '../../context/GetAllColumnContext';
import Spinners from '../Spinner';
import styles from '../Spinner.module.css';
import headerStyles from './GridHeader.module.css';
import {
  compareEditedRowObjects,
  compareAddedRowObjects,
} from '../../../shared/utils/commitHandlerHelper';
import {
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarExportContainer,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-premium';
import { ExportMenuItem } from '../excel-export/ExportMenuItem';
import ViewManger from './viewManager';
import { useLanguageContextAPI } from '../../context/LanguageContext';
import { useUserContextApi } from '../../context/UserContext';
import { t } from '../../../i18next';
import { useConfigContext } from '../../context/ConfigContext';
import { useLeavePageConfirmation } from '../../utils/useLeavePageConfirmation';
import SaveChangesIcon from '../SaveChangesIcon';
import { useSvcWorkerContext } from '../../context/ServiceWorkerContext';
import { Input } from '@airbus/components-react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import ConfirmationModal from './ConfirmationModal';
import ResponsiveActions from './ResponsiveActions';
import { useMediaQuery } from '@mui/material';

const ViewsList = lazy(() => import('./viewManager/ViewsList'));

export function EditToolbar(props) {
  const {
    setrows,
    rows,
    apiRef,
    rowselectionmodel,
    setRowSelectionModel,
    setrowclicked,
    isformpanelopen,
    setAlertbar,
    setAccordionDetails,
    isRowLoading,
    quickSearchAPI,
  } = props;

  const {
    accessDetails: access,
    allAccessibleColumns,
    updatereRender,
    updateCustomReRender,
    tableDetails,
    customConfig,
  } = useColumnContextAPI();
  const { userDetails } = useUserContextApi();
  const { language } = useLanguageContextAPI();
  const { Actions, AdditionalHeaderComp, ScreenSlotProps, AdditionalHeaderForScreens } =
    customConfig;
  const [openConfirmDeleteBox, setOpenConfirmDeleteBox] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [openSaveBox, setOpenSaveBox] = React.useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [commitConfirmationProps, setCommitConfirmationProps] = useState(null);
  const [deleteIds, setDeletedRow] = useState([]);
  const [addedRow, setAddedRow] = useState([]);
  const [editedRow, setEditedRow] = useState([]);
  const [editedRowOriginal, setEditedRowOriginal] = useState({});
  const { mbdDataGrid } = tableDetails;
  const [openSpinner, setOpenSpinner] = useState(false);
  const { configSchema } = useConfigContext();
  const [shouldPreventLeaving, setShouldPreventLeaving] = useState(false);
  const [, setExportInProgress] = useState(false);
  const { isReportInProgress } = useSvcWorkerContext();
  const [searchText, setSearchText] = useState('');
  const serverSideQuickSearch = useFeatureFlagEnabled('serverSideQuickSearch');
  const isMobile = useMediaQuery('(max-width:430px)');
  const toolbarHostRef = React.useRef<HTMLDivElement | null>(null);
  const [sortTooltip, setSortTooltip] = React.useState({
    open: false,
    label: '',
    left: 0,
    top: 0,
    placement: '',
  });

  type TooltipR<T> = Partial<T> & Record<string, unknown>;

  useEffect(() => {
    const removeAllTooltips = () => {
      document.querySelectorAll('.MuiTooltip-popper, .MuiTooltip-root').forEach((n) => {
        (n as HTMLElement).style.display = 'none';
      });

      document
        .querySelectorAll('.MuiDataGrid-root [title], [role="button"][title]')
        .forEach((el) => el.removeAttribute('title'));
    };

    removeAllTooltips();

    const observer = new MutationObserver(() => removeAllTooltips());
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const gridRoot = document.querySelector('.MuiDataGrid-root');
    if (!gridRoot) return;

    const onEnter = (e: MouseEvent) => {
      const target = (e.currentTarget ?? e.target) as HTMLElement;
      const rect = target.getBoundingClientRect();

      const hostRect = toolbarHostRef.current?.getBoundingClientRect();
      const left = hostRect
        ? rect.left - hostRect.left + rect.width / 2
        : rect.left + rect.width / 2;
      const top = hostRect ? rect.bottom - hostRect.top : rect.bottom;

      setSortTooltip({
        open: true,
        label: 'Sort',
        left,
        top,
        placement: 'bottom',
      });
    };

    const onLeave = () => {
      setSortTooltip((s) => ({ ...s, open: false }));
    };

    const attach = () => {
      gridRoot.querySelectorAll('.MuiDataGrid-sortIcon').forEach((el) => {
        if (!(el as HTMLElement).dataset?.airbusTooltipAttached) {
          (el as HTMLElement).dataset.airbusTooltipAttached = '1';
          el.addEventListener('mouseenter', onEnter);
          el.addEventListener('focus', onEnter);
          el.addEventListener('mouseleave', onLeave);
          el.addEventListener('blur', onLeave);
        }
      });
    };

    attach();

    const observer = new MutationObserver(() => {
      Promise.resolve().then(attach);
    });
    observer.observe(gridRoot, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      gridRoot.querySelectorAll('.MuiDataGrid-sortIcon').forEach((el) => {
        if ((el as HTMLElement).dataset?.airbusTooltipAttached) {
          el.removeEventListener('mouseenter', onEnter);
          el.removeEventListener('focus', onEnter);
          el.removeEventListener('mouseleave', onLeave);
          el.removeEventListener('blur', onLeave);
          delete (el as HTMLElement).dataset.airbusTooltipAttached;
        }
      });
      setSortTooltip((s) => ({ ...s, open: false }));
    };
  }, [toolbarHostRef]);

  useEffect(() => {
    const dirtyRows = rows?.filter((r) => ['N', 'E', 'C', 'D'].includes(r.status)) || [];

    const hasUnsavedChanges = dirtyRows.length > 0;

    setShouldPreventLeaving(hasUnsavedChanges);
  }, [rows]);

  useLeavePageConfirmation({ shouldPreventLeaving });

  const handleCommitChanges = async () => {
    setOpenSaveBox(false);
    setOpenConfirmationModal(false);
    setCommitConfirmationProps(null);

    if (customConfig?.CommitHandler) {
      setOpenSpinner(true);
      const { CommitHandler } = customConfig;
      const commitResult = await CommitHandler({
        addedRow,
        editedRow,
        editedRowOriginal,
        deletedRow: deleteIds,
        allAccessibleColumns,
        commitSuccessMsgHandler,
        tableDetails,
        updatereRender,
        updateCustomReRender,
        setAlertbar,
        setOpenSpinner,
        userDetails,
        configSchema,
        ...ScreenSlotProps,
      });
      setOpenSpinner(false);

      if (commitResult?.errors?.length) {
        setAlertbar({
          children: t('error.action_failed_following', {
            msg: commitResult.errors[0].message || commitResult.errors,
          }),
          severity: 'error',
        });
      } else {
        commitSuccessMsgHandler(commitResult);
        updatereRender();

        if (commitResult?.warnings?.length) {
          const warningMessages = [
            ...new Set(
              commitResult.warnings
                .map((warning) => warning?.message)
                .filter((message) => typeof message === 'string' && message.trim().length > 0),
            ),
          ];

          warningMessages.forEach((message) => {
            setAlertbar({
              children: message,
              severity: 'warning',
            });
          });
        }
      }
      return;
    }

    const updateObjArr = editedRow.map((item) => {
      const where = {};
      where[mbdDataGrid.dataGridUniqueIdentifier] = {
        _eq: item[mbdDataGrid.dataGridUniqueIdentifier],
      };
      const setData = compareEditedRowObjects(
        item,
        editedRowOriginal,
        mbdDataGrid.dataGridUniqueIdentifier,
      );
      setData['updated_by'] = userDetails.userId;
      setData['update_date'] = new Date().toISOString().substring(0, 19);
      return {
        where,
        _set: setData,
      };
    });

    const addObjArr = compareAddedRowObjects(addedRow);
    addObjArr.forEach((item) => (item.created_by = userDetails.userId));

    try {
      const result = await postRequest('/api/tables', {
        screenGridId: tableDetails.id,
        tables: [
          {
            schema: mbdDataGrid.schemaName,
            table: mbdDataGrid.dataGridSource,
            pkey: mbdDataGrid.dataGridUniqueIdentifier,
            deleteIds,
            insertObjects: addObjArr,
            updateObjects: updateObjArr,
          },
        ],
      });
      if (result?.errors) {
        setAlertbar({
          children: t('error.action_failed_following', {
            msg: result.errors[0].message || result.errors,
          }),
          severity: 'error',
        });
        return;
      }
      resetActions();
    } catch {
      setAlertbar({
        children: t('exception_occurred'),
        severity: 'error',
      });
    }
  };

  const resetActions = () => {
    commitSuccessMsgHandler(undefined);
    setAddedRow([]);
    setEditedRow([]);
    setAccordionDetails(null);
    updateCustomReRender();
    updatereRender();
  };

  const commitSuccessMsgHandler = (commitResult) => {
    const exportState = apiRef.current.exportState();
    const isFilterApplied = exportState.filter.filterModel.items.length;
    const isGlobalSearchApplied = exportState.filter.filterModel?.quickFilterValues?.length ?? 0;
    const isSortingApplied = exportState.sorting.sortModel.length;
    const currPage = exportState.pagination.paginationModel.page;

    const messagesToShow = new Set();

    //if just sorting is applied.
    if (isSortingApplied > 0 && isGlobalSearchApplied === 0 && isFilterApplied === 0) {
      //if sorting order is desc, then row is not added at top thus show alertbar
      //on initial loading, id is coming as sorted in desc order. Although row is adding at top still this alertbar is showing because its order is desc. So checking this condition in below
      const descSort = exportState.sorting.sortModel?.filter(
        (i) => i.field !== 'id' && i.sort === 'desc',
      );
      const ascSort = exportState.sorting.sortModel?.filter((i) => i.sort === 'asc');

      if (descSort.length > 0 || (ascSort.length > 0 && currPage > 0)) {
        messagesToShow.add(t('successfully_saved_noshow'));
      } else if (commitResult?.allTableSuccess === false) {
        messagesToShow.add(t('mob.esaph_alert'));
      } else {
        messagesToShow.add(t('successfully_saved'));
      }
    }

    if (isGlobalSearchApplied > 0 || isFilterApplied > 0) {
      messagesToShow.add(t('successfully_saved_noshow'));
    } else if (commitResult?.allTableSuccess === false) {
      messagesToShow.add(t('mob.esaph_alert'));
    } else {
      messagesToShow.add(t('successfully_saved'));
    }

    for (const message of messagesToShow) {
      setAlertbar({
        children: message,
        severity: 'success',
      });
    }

    setOpenSpinner(false);
  };

  const addNewRow = () => {
    let id = 1;
    if (rows.length) {
      const sortedRows = [...rows];
      sortedRows.sort(compareId);
      const lastId = sortedRows[sortedRows.length - 1].id;
      id += lastId;
    }

    const baseNewRow = allAccessibleColumns.reduce(
      (newRow, obj) => {
        const { attrInternalName: columnName, attrDuplicateConfig: duplicateValue } = obj;

        newRow[columnName] = null;
        if (columnName === mbdDataGrid.dataGridUniqueIdentifier) {
          newRow[columnName] = null;
        } else if (duplicateValue && duplicateValue !== 'copy-existing') {
          newRow[columnName] = isNaN(duplicateValue) ? duplicateValue : +duplicateValue;
        }
        return newRow;
      },
      {} as Record<string, any>,
    );

    // allow screens to initialize new rows
    const initializedRow =
      typeof customConfig?.NewRowInitializer === 'function'
        ? customConfig.NewRowInitializer({
            row: { ...baseNewRow, id, touched: {}, status: 'N' },
            allAccessibleColumns,
            tableDetails,
          })
        : { ...baseNewRow, id, touched: {}, status: 'N' };

    setrows((oldRows) => [initializedRow, ...oldRows]);

    setTimeout(() => {
      if (apiRef?.current && apiRef.current.scrollToIndexes) {
        apiRef.current.scrollToIndexes({ rowIndex: 0 });
      }
      const gridScroller = document.querySelector(
        '.MuiDataGrid-scrollbar, .MuiDataGrid-scrollbar--vertical',
      );
      if (gridScroller) (gridScroller as HTMLElement).scrollTop = 0;
    }, 0);

    if (isformpanelopen && (access?.single_dataset || access?.singleDatasetGroup)) {
      setrowclicked(initializedRow);
    }
  };

  const duplicateRowHandler = () => {
    if (!rowselectionmodel.length) {
      return;
    }
    const filterSelectedRow = rows.filter((rowItem) => {
      return rowselectionmodel.includes(rowItem.id);
    });
    const sortedRows = [...rows];
    sortedRows.sort(compareId);
    const lastId = sortedRows[sortedRows.length - 1].id;
    const duplicatingRows = getDuplicateRows({
      filteredRows: filterSelectedRow,
      allAccessibleColumns,
      mbdDataGrid,
      lastId,
    });
    setrows((oldRows) => [...duplicatingRows, ...oldRows]);

    if (isformpanelopen && (access?.singledataset || access?.singleDatasetGroup)) {
      setrowclicked(duplicatingRows[0]);
    }
  };

  const proceedDeleteHandler = () => {
    if (!rowselectionmodel.length) {
      return;
    }
    const updatedRows = rows.reduce((acc, obj) => {
      if (rowselectionmodel.includes(obj.id)) {
        if (obj.status !== 'N' && obj.status !== 'C') {
          acc.push({ ...obj, status: 'D' });
        }
      } else {
        acc.push({ ...obj });
      }
      return acc;
    }, []);

    setAccordionDetails((prev) => prev?.filter((detail) => !rowselectionmodel.includes(detail.id)));
    setOpenConfirmDeleteBox(false);
    setrows(updatedRows);
    if (isformpanelopen) {
      const checkId = rowselectionmodel[0];
      const lastRecord = updatedRows.find((item) => item.id === checkId);
      setRowSelectionModel([lastRecord?.id ?? updatedRows[0]?.id]);
      setrowclicked(lastRecord ?? updatedRows[0]);
    }
  };

  const proceedSaveHandler = async () => {
    let deletedRow = rows.filter((i) => i?.status === 'D').map((i) => i?.id);
    if (customConfig?.CommitHandler) {
      deletedRow = rows.filter((i) => i?.status === 'D');
    }
    setDeletedRow(deletedRow);
    try {
      setOpenSpinner(true);
      const { add, edit, errors, editOG } = await splitAllRows({
        rows,
        allColumnDefs: allAccessibleColumns,
        pkeyColumn: mbdDataGrid.dataGridUniqueIdentifier,
        SetISCellRequired: customConfig?.SetISCellRequired,
        language,
        customConfig,
        access,
        tableDetails,
        roleIds: userDetails.roleIds,
      });

      if (errors.length) {
        setAccordionDetails(errors);
        return;
      }
      setAccordionDetails(null);
      if (deletedRow?.length === 0 && add?.length === 0 && edit?.length === 0) {
        setAlertbar({
          children: t('no_changes_found'),
          severity: 'error',
        });
      } else {
        setAddedRow(() => [...add]);
        setEditedRow(() => [...edit]);
        setEditedRowOriginal(editOG);
        if (customConfig?.ConfirmBeforeCommit) {
          const result = await customConfig
            .ConfirmBeforeCommit(
              {
                add,
                edit,
                editRowOriginal: editOG,
              },
              tableDetails,
              {
                accessDetails: access,
                roleIds: userDetails.roleIds,
              },
              allAccessibleColumns,
            )
            .catch(() => ({}));

          if (result?.shouldOpenConfirmation) {
            setCommitConfirmationProps(result.confirmationProps);
            setOpenConfirmationModal(true);
            return;
          }
        }
        setOpenSaveBox(true);
      }
    } catch (error) {
      setAlertbar({
        children: t('error.exception', { msg: error.message }),
        severity: 'error',
      });
    } finally {
      setOpenSpinner(false);
    }
  };

  const deleteRecordHandler = () => {
    const check = rows.filter((i) => i.id === rowselectionmodel[0] && i.status === 'D');
    if (rowselectionmodel.length === 0 || check.length > 0) {
      setAlertbar({
        children: t('no_row_selected'),
        severity: 'error',
      });
      return;
    }
    setOpenConfirmDeleteBox(true);
  };
  const handleOpen = () => setOpen(true);

  const getExportMenuItem = () => {
    if (isReportInProgress()) {
      return (
        <>
          <Divider vertical />
          <Spinner />
        </>
      );
    }

    return (
      <>
        <Tooltip label={t('export')}>
          <GridToolbarExportContainer
            slotProps={{
              button: {
                className: headerStyles.toolbarButtons,
                disabled: isRowLoading,
              },
              tooltip: {
                disableHoverListener: true,
                disableFocusListener: true,
                disableTouchListener: true,
              },
            }}
          >
            <ExportMenuItem
              data-testid={`${mbdDataGrid.dataGridSource}_toolbar_export`}
              setAlertbar={setAlertbar}
              setExportInProgress={setExportInProgress}
            />
          </GridToolbarExportContainer>
        </Tooltip>
      </>
    );
  };

  const featuresComponents = [
    {
      key: access?.add_row?.featureName,
      component: access?.add_row && (
        <Tooltip label={t('add')}>
          <IconButton
            variant='ghost'
            size='small'
            onClick={addNewRow}
            disabled={isRowLoading}
            data-testid={`${mbdDataGrid.dataGridSource}_btn_addRow`}
          >
            <Add />
          </IconButton>
        </Tooltip>
      ),
      sortOrder: access?.add_row?.sortOrderNo,
    },
    {
      key: access?.clone_row?.featureName,
      component: access?.clone_row && (
        <Tooltip label={t('duplicate')}>
          <IconButton
            variant='ghost'
            size='small'
            onClick={duplicateRowHandler}
            disabled={isRowLoading || !rowselectionmodel.length}
            data-testid={`${mbdDataGrid.dataGridSource}_btn_clone`}
          >
            <ContentCopy />
          </IconButton>
        </Tooltip>
      ),
      sortOrder: access?.clone_row?.sortOrderNo,
    },
    {
      key: access?.delete_row?.featureName,
      component: access?.delete_row && (
        <Tooltip label={t('delete')}>
          <IconButton
            variant='ghost'
            size='small'
            onClick={deleteRecordHandler}
            disabled={isRowLoading || !rowselectionmodel.length}
            data-testid={`${mbdDataGrid.dataGridSource}_btn_delete`}
          >
            <Delete />
          </IconButton>
        </Tooltip>
      ),
      sortOrder: access?.delete_row?.sortOrderNo,
    },
    {
      key: access?.save_data?.featureName,
      component: access?.save_data && (
        <Tooltip label={t('save')}>
          <SaveChangesIcon
            isRowLoading={isRowLoading}
            shouldPreventLeaving={shouldPreventLeaving}
            onClick={proceedSaveHandler}
            testId={`${mbdDataGrid.dataGridSource}_btn_save`}
          />
        </Tooltip>
      ),
      sortOrder: access?.save_data?.sortOrderNo,
    },
  ];

  const actionsProps = React.useMemo(
    () => ({
      ...props,
      access,
      tableDetails,
      mbdDataGrid,
      editedRowOriginal,
      rowselectionmodel: props.rowselectionmodel ?? [],
      tableFeatureData: (props as any).tableFeatureData ?? {},
    }),
    [props, access, tableDetails, mbdDataGrid, editedRowOriginal],
  );

  const sortedFeaturesComponents = featuresComponents
    .filter((action) => action.key)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((action) => <React.Fragment key={action.key}>{action.component}</React.Fragment>);

  return (
    <div ref={toolbarHostRef} style={{ position: 'relative' }}>
      <GridToolbarContainer>
        {openSpinner && (
          <div className={styles.spinners}>
            <ColorModeProvider mode='light'>
              <Spinners />
            </ColorModeProvider>
          </div>
        )}
        <Inline
          alignY='center'
          spacing='quarter-x'
          align='end'
          wrap
          className={headerStyles.headerToolbarContainer}
          childrenClassName={headerStyles.headerToolbarItem}
        >
          <div>
            {access?.quickSearch &&
              (serverSideQuickSearch && quickSearchAPI ? (
                <div className={headerStyles.searchAction}>
                  <div className={headerStyles.searchContainer}>
                    <Input
                      value={searchText}
                      className={headerStyles.searchInput}
                      onChange={(e) => {
                        const query = e.target.value;
                        setSearchText(query);
                        if (query.trim() === '') {
                          quickSearchAPI('');
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const input = e.target as HTMLInputElement;
                          const query = input.value.trim();
                          quickSearchAPI(query);
                        }
                      }}
                      placeholder='Search…'
                      endAdornment={
                        <IconButton
                          aria-label='Search'
                          size='small'
                          variant='ghost'
                          onClick={() => {
                            const query = searchText.trim();
                            quickSearchAPI(query);
                          }}
                        >
                          <Search />
                        </IconButton>
                      }
                    />
                  </div>
                </div>
              ) : (
                <div className={headerStyles.searchFilter}>
                  <GridToolbarQuickFilter
                    sx={{ width: 200 }}
                    data-testid={`${mbdDataGrid.dataGridSource}_toolbar_quickFilter`}
                  />
                </div>
              ))}
          </div>
          <div className={headerStyles.actionContainer}>
            {Actions && (
              <div className={headerStyles.actionsRow}>
                <ResponsiveActions
                  Actions={Actions}
                  actionsProps={actionsProps}
                  isMobile={isMobile}
                />
                {(access?.viewManagement ||
                  sortedFeaturesComponents.length > 0 ||
                  access?.filtering ||
                  access?.dataExport ||
                  AdditionalHeaderComp?.length > 0 ||
                  AdditionalHeaderForScreens) && (
                  <Divider vertical className={headerStyles.divideLine} />
                )}
              </div>
            )}
          </div>

          <div className={headerStyles.standardView}>
            {access?.viewManagement && (
              <Inline
                alignY='center'
                align='center'
                spacing='quarter-x'
                childrenClassName={headerStyles.displayInlineFlex}
              >
                <Suspense fallback={<Spinner />}>
                  <ViewsList
                    isRowLoading={isRowLoading}
                    data-testid={`${mbdDataGrid.dataGridSource}_toolbar_viewList`}
                  />
                </Suspense>
                <Inline
                  alignY='center'
                  align='center'
                  spacing='quarter-x'
                  childrenClassName={headerStyles.displayInlineFlex}
                >
                  <IconButton
                    variant='ghost'
                    size='small'
                    onClick={handleOpen}
                    disabled={isRowLoading}
                    data-testid={`${mbdDataGrid.dataGridSource}_toolbar_viewManager`}
                  >
                    <Tooltip label={t('view.view_manager')}>
                      <SettingsIcon className={headerStyles.viewManagerIcon} />
                    </Tooltip>
                  </IconButton>
                </Inline>
              </Inline>
            )}
            {access?.viewManagement &&
              (sortedFeaturesComponents.length > 0 ||
                access?.filtering ||
                access?.dataExport ||
                AdditionalHeaderComp?.length > 0 ||
                AdditionalHeaderForScreens) && (
                <Divider vertical className={headerStyles.dividerFlex} />
              )}
          </div>
          <div className={headerStyles.iconContainer}>
            {AdditionalHeaderComp?.map(({ name, Component }) => (
              <div key={name} className={headerStyles.w250}>
                <Component {...({ ...ScreenSlotProps, rows } as any)} />
              </div>
            ))}
            {AdditionalHeaderForScreens && (
              <div className={headerStyles.w250}>{AdditionalHeaderForScreens}</div>
            )}

            <div className={headerStyles.iconButtons}>
              {(access?.clone_row || access?.delete_row || access?.save_data || access?.add_row) &&
                sortedFeaturesComponents}
              {(access?.filtering || access?.dataExport) && (
                <Inline
                  alignY='center'
                  align='center'
                  spacing='quarter-x'
                  childrenClassName={headerStyles.displayInlineFlex}
                >
                  {access?.filtering && (
                    <Tooltip label='Filter'>
                      <GridToolbarFilterButton
                        data-testid={`${mbdDataGrid.dataGridSource}_toolbar_filter`}
                        slotProps={{
                          button: {
                            className: headerStyles.toolbarButtons,
                            sx: { minWidth: 'unset', borderRadius: '50%', p: 1 },
                          },
                          tooltip: {
                            disableHoverListener: true,
                            disableFocusListener: true,
                            disableTouchListener: true,
                          },
                        }}
                      />
                    </Tooltip>
                  )}
                  {access?.filtering && access?.dataExport && (
                    <div className={headerStyles.dividerFlex}>
                      <Divider vertical />
                    </div>
                  )}
                  {access?.dataExport && getExportMenuItem()}
                </Inline>
              )}
            </div>
          </div>
        </Inline>
        <CommitConfirmBox {...{ openSaveBox, setOpenSaveBox, mbdDataGrid, handleCommitChanges }} />
        {openConfirmationModal && (
          <ConfirmationModal
            {...{
              openConfirmationModal,
              setOpenConfirmationModal,
              shouldOpenConfirmation: !!commitConfirmationProps,
              mbdDataGrid,
              onConfirm: handleCommitChanges,
              onCancel: () => setOpenConfirmationModal(false),
              beforeCommit: commitConfirmationProps?.beforeCommit,
              setCommitConfirmationProps,
              commitConfirmationProps,
            }}
          />
        )}
        <DeleteConfirmBox
          {...{
            openConfirmDeleteBox,
            setOpenConfirmDeleteBox,
            rowselectionmodel,
            mbdDataGrid,
            proceedDeleteHandler,
          }}
        />
        <ViewManger {...{ apiRef, open, setOpen, mbdDataGrid }} />
      </GridToolbarContainer>
      {sortTooltip.open && (
        <div
          style={{
            position: 'absolute',
            left: sortTooltip.left,
            top: sortTooltip.top - 15,
          }}
          aria-hidden
        >
          <Tooltip
            {...({
              label: 'Sort',
              open: true,
              placement: 'bottom',
            } as TooltipR<React.ComponentProps<typeof Tooltip>>)}
          >
            <span style={{ display: 'inline-block', width: 8, height: 8, pointerEvents: 'none' }} />
          </Tooltip>
          {/* <Tooltip
            label='Sort'
            // @ts-ignore
            open={true}
          >
            <span style={{ display: 'inline-block', width: 8, height: 8, pointerEvents: 'none' }} />
          </Tooltip> */}
        </div>
      )}
    </div>
  );
}

function CommitConfirmBox(props) {
  const { openSaveBox, setOpenSaveBox, mbdDataGrid, handleCommitChanges } = props;
  const cancelSaveHandler = () => {
    setOpenSaveBox(false);
  };
  return (
    <Dialog open={openSaveBox} onClose={cancelSaveHandler} aria-describedby='alert-dialog-save'>
      <DialogHeader>{t('do_you_want_save')}</DialogHeader>

      <DialogActions>
        <Button
          onClick={cancelSaveHandler}
          variant='secondary'
          data-testid={`${mbdDataGrid.dataGridSource}_saveConfirm_btn_cancel`}
        >
          {t('cancel')}
        </Button>
        <Button
          onClick={handleCommitChanges}
          variant='primary'
          data-testid={`${mbdDataGrid.dataGridSource}_saveConfirm_btn_ok`}
        >
          {t('ok')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DeleteConfirmBox(props) {
  const {
    openConfirmDeleteBox,
    setOpenConfirmDeleteBox,
    rowselectionmodel,
    mbdDataGrid,
    proceedDeleteHandler,
  } = props;
  const cancelDeleteHandler = () => {
    setOpenConfirmDeleteBox(false);
  };

  return (
    <Dialog
      open={openConfirmDeleteBox}
      onClose={cancelDeleteHandler}
      aria-describedby='alert-dialog-slide-description'
    >
      <DialogHeader>
        {t('delete_selected_row', { multi: rowselectionmodel.length > 1 ? 's' : '' })}
      </DialogHeader>
      <DialogActions>
        <Button
          onClick={cancelDeleteHandler}
          variant='secondary'
          data-testid={`${mbdDataGrid.dataGridSource}_deleteConfirm_btn_cancel`}
        >
          {t('cancel')}
        </Button>
        <Button
          onClick={proceedDeleteHandler}
          variant='primary'
          data-testid={`${mbdDataGrid.dataGridSource}_deleteConfirm_btn_ok`}
        >
          {t('ok')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export const splitAllRows = async (props) => {
  const {
    rows,
    allColumnDefs,
    pkeyColumn,
    SetISCellRequired,
    language,
    customConfig,
    access,
    tableDetails,
    roleIds,
  } = props;
  return await rows.reduce(
    async (acc, row, index) => {
      const resp = await acc;
      return await processRow({
        resp,
        row,
        index,
        allColumnDefs,
        pkeyColumn,
        SetISCellRequired,
        language,
        customConfig,
        access,
        tableDetails,
        originalRows: rows,
        roleIds,
      });
    },
    { add: [], edit: [], editOG: {}, errors: [], allRows: [] },
  );
};

async function processRow({
  resp,
  row,
  index,
  allColumnDefs,
  pkeyColumn,
  SetISCellRequired,
  language,
  customConfig,
  access,
  tableDetails,
  originalRows,
  roleIds,
}) {
  const { isNC, isNCE } = getStatusProp(row.status);
  const formattedRow = formatDateMaker(row);
  const newRow = filterkeys(formattedRow, pkeyColumn, allColumnDefs);
  const backupRow = isNC ? null : filterkeys(row.backup, pkeyColumn, allColumnDefs);
  const updatedRow = filteredRow(access, row);

  if (isNCE) {
    if (
      !(await validateRow({
        resp,
        row,
        index,
        updatedRow,
        allColumnDefs,
        SetISCellRequired,
        language,
        customConfig,
        pkeyColumn,
        rows: resp.allRows,
        tableDetails,
        originalRows,
        access,
        roleIds,
      }))
    ) {
      return resp;
    }

    resp.allRows.push({ ...row, isCommitRow: true });

    if (isNC) {
      resp.add.push(newRow);
      return resp;
    }

    resp.editOG[row.id] = backupRow;
    resp.edit.push(newRow);

    return resp;
  }
  resp.allRows.push(row);
  return resp;
}

const validateRow = async ({
  resp,
  row,
  index,
  updatedRow,
  allColumnDefs,
  SetISCellRequired,
  language,
  customConfig,
  pkeyColumn,
  rows,
  tableDetails,
  originalRows,
  access,
  roleIds,
}) => {
  const rowNum = index + 1;
  const getSchema = await customValidator({
    allColumnDefs,
    row: updatedRow,
    SetISCellRequired,
    language,
  });

  if (!getSchema.isValid) {
    addValidationError(resp, getSchema.errors, row.id, rowNum);
    return false;
  }

  if (customConfig?.uniqueValidator) {
    const validateResult = customConfig.uniqueValidator(rows, row, pkeyColumn);
    if (!validateResult.isValid) {
      addValidationError(resp, validateResult.errors, row.id, rowNum);
      return false;
    }
  }

  if (customConfig?.extraValidator) {
    const validateResult = await customConfig.extraValidator({
      row,
      rows: rows,
      tableDetails,
      allColumnDefs,
      language,
      originalRows,
      access,
      roleIds,
    });
    if (!validateResult.isValid) {
      addValidationError(resp, validateResult.errors, row.id, rowNum);
      return false;
    }
  }

  return true;
};

const addValidationError = (resp, errors, id, rowNum) => {
  resp.errors.push({
    column: errors,
    id,
    rowno: 'Row ' + rowNum,
    rowIndex: rowNum,
  });
};

const getStatusProp = (status) => {
  const isNC = !!(status === 'N' || status === 'C');
  const isNCE = !!(status === 'N' || status === 'C' || status === 'E');
  return { isNC, isNCE };
};

const formatDateMaker = (row) => {
  for (const [key, value] of Object.entries(row)) {
    if (Object.prototype.toString.call(value) === '[object Date]') {
      row[key] = formatDate(value);
    }
  }
  return row;
};

export const filterkeys = (row, pkeyColumn, allColumnDefs) => {
  const pKeyValidation = (pKeyId) => {
    const validations = allColumnDefs.find((item) => {
      return item.attrInternalName === pKeyId && item.editable && item.attrMandatory;
    });
    const isValid = validations == undefined;
    return isValid;
  };
  const { isNC } = getStatusProp(row.status);
  const arr = ['status', 'touched', 'backup', 'isCommitRow'];
  if (isNC) {
    arr.push('id');
    if (pKeyValidation(pkeyColumn)) {
      arr.push(pkeyColumn);
    }
  } else if (pkeyColumn != 'id') {
    arr.push('id');
  }
  const newObj = { ...row };
  for (const item of arr) {
    delete newObj[item];
  }
  return newObj;
};

const filteredRow = (access, row) => {
  const filteredRow = {};
  Object.keys(row).forEach((key) => {
    const findKey = Object.keys(access).includes(key);
    if (findKey === true) {
      filteredRow[key] = row[key];
    }
  });
  return filteredRow;
};

export const getDuplicateRows = (props) => {
  const { filteredRows, allAccessibleColumns, mbdDataGrid, lastId } = props;
  return filteredRows.map((obj, index) => {
    const cloneObj = { ...obj };
    for (const element of allAccessibleColumns) {
      const { attrInternalName: columnName, attrDuplicateConfig: duplicateValue } = element;

      if (columnName === mbdDataGrid.dataGridUniqueIdentifier) {
        cloneObj[columnName] = null;
      } else if (duplicateValue && duplicateValue !== 'copy-existing') {
        cloneObj[columnName] = isNaN(duplicateValue) ? duplicateValue : +duplicateValue;
      } else if (!duplicateValue) {
        cloneObj[columnName] = null;
      }
    }
    const id = lastId + index + 1;
    return { ...cloneObj, status: 'C', touched: {}, id };
  });
};
function compareId(a, b) {
  const bandA = a.id;
  const bandB = b.id;

  let comparison = 0;
  if (bandA > bandB) {
    comparison = 1;
  } else if (bandA < bandB) {
    comparison = -1;
  }
  return comparison;
}
